import { useState, useEffect, useCallback } from 'react';
import { getUserDeliveries, addDeliveryAddress, updateDeliveryAddress } from '../utils/deliveryUtils';
import { createLogger } from '../utils/logger';

const logger = createLogger('useCheckoutDelivery');

/**
 * Delivery option / address / phone state for checkout.
 */
export function useCheckoutDelivery({ currentUser, supermarket }) {
  const [deliveryOption, setDeliveryOption] = useState('pickup');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryPhone, setDeliveryPhone] = useState('');
  const [deliveryAddressConfirmed, setDeliveryAddressConfirmed] = useState(false);
  const [userDeliveries, setUserDeliveries] = useState([]);
  const [selectedDeliveryId, setSelectedDeliveryId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadDeliveries = async () => {
      if (!currentUser) return;

      try {
        const deliveries = await getUserDeliveries(currentUser.uid);
        if (cancelled) return;

        setUserDeliveries(deliveries);

        if (deliveries.length > 0) {
          const defaultDelivery = deliveries.find((d) => d.isDefault) || deliveries[0];
          setSelectedDeliveryId(defaultDelivery.id);
          setDeliveryAddress(defaultDelivery.address);
          setDeliveryPhone(defaultDelivery.phone || '');

          // Auto-select delivery when supermarket supports it, but do NOT auto-confirm
          // fee/distance acknowledgment — the user must check that box explicitly.
          if (supermarket?.delivery_enabled) {
            setDeliveryOption('delivery');
          }
        }
      } catch (error) {
        logger.error('[useCheckoutDelivery] Error fetching delivery data:', error);
        if (!cancelled) setUserDeliveries([]);
      }
    };

    loadDeliveries();
    return () => {
      cancelled = true;
    };
  }, [currentUser, supermarket?.delivery_enabled]);

  const selectSavedDelivery = useCallback(
    (deliveryId) => {
      setSelectedDeliveryId(deliveryId);
      if (deliveryId) {
        const selectedDelivery = userDeliveries.find((d) => d.id === deliveryId);
        if (selectedDelivery) {
          setDeliveryAddress(selectedDelivery.address);
          setDeliveryPhone(selectedDelivery.phone || '');
        }
      } else {
        setDeliveryAddress('');
        setDeliveryPhone('');
      }
    },
    [userDeliveries]
  );

  /**
   * Persist a new address, or update phone on an existing saved address.
   * Treats null / '' selectedDeliveryId as "new address" (the select uses '').
   */
  const persistDeliveryIfNeeded = useCallback(async () => {
    if (deliveryOption !== 'delivery' || !currentUser) return;

    // Fix: UI sets selectedDeliveryId to '' for "Enter new address"; previously
    // only null was treated as new, so new addresses were never saved.
    if (!selectedDeliveryId) {
      await addDeliveryAddress(currentUser.uid, deliveryAddress, deliveryPhone);
      return;
    }

    const existingDelivery = userDeliveries.find((d) => d.id === selectedDeliveryId);
    if (existingDelivery) {
      const existingPhone = existingDelivery.phone || '';
      if (deliveryPhone.trim() !== existingPhone.trim()) {
        await updateDeliveryAddress(selectedDeliveryId, { phone: deliveryPhone.trim() });
      }
    }
  }, [
    currentUser,
    deliveryOption,
    deliveryAddress,
    deliveryPhone,
    selectedDeliveryId,
    userDeliveries,
  ]);

  const validateDeliveryForPayment = useCallback(
    ({ hasConfiguredDeliveryFee }) => {
      if (deliveryOption !== 'delivery') {
        return { ok: true };
      }
      if (!deliveryAddressConfirmed) {
        return {
          ok: false,
          type: 'warning',
          title: 'Confirmation Required',
          message: hasConfiguredDeliveryFee
            ? 'Please confirm that you understand the delivery fee and distance policy.'
            : 'Please confirm that you understand the delivery fee arrangement.',
        };
      }
      if (!deliveryAddress.trim()) {
        return {
          ok: false,
          type: 'warning',
          title: 'Address Required',
          message: 'Please enter your delivery address.',
        };
      }
      if (!deliveryPhone.trim()) {
        return {
          ok: false,
          type: 'warning',
          title: 'Phone Required',
          message: 'Please enter your delivery phone number.',
        };
      }
      return { ok: true };
    },
    [deliveryOption, deliveryAddressConfirmed, deliveryAddress, deliveryPhone]
  );

  return {
    deliveryOption,
    setDeliveryOption,
    deliveryAddress,
    setDeliveryAddress,
    deliveryPhone,
    setDeliveryPhone,
    deliveryAddressConfirmed,
    setDeliveryAddressConfirmed,
    userDeliveries,
    selectedDeliveryId,
    selectSavedDelivery,
    persistDeliveryIfNeeded,
    validateDeliveryForPayment,
  };
}
